/** Normalized bounds of the visible pixels inside an image. */
export interface ImageContentBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Ignore almost invisible alpha noise, retaining a small margin for soft edges.
 * Opaque photos keep their full frame: white pixels may be part of the product.
 */
export function findImageContentBounds(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
): ImageContentBounds | null {
  if (width <= 0 || height <= 0 || pixels.length !== width * height * 4) return null;
  let left = width, top = height, right = -1, bottom = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (pixels[(y * width + x) * 4 + 3] < 8) continue;
      left = Math.min(left, x);
      right = Math.max(right, x);
      top = Math.min(top, y);
      bottom = Math.max(bottom, y);
    }
  }
  if (right < left) return null;
  // Two sample pixels protect antialiasing and downsampled details.
  left = Math.max(0, left - 2);
  top = Math.max(0, top - 2);
  right = Math.min(width, right + 3);
  bottom = Math.min(height, bottom + 3);
  return { x: left / width, y: top / height, width: (right - left) / width, height: (bottom - top) / height };
}

/** Contain the visible product, then offset the original image around it. */
export function fitProductImage(
  imageWidth: number,
  imageHeight: number,
  frameWidth: number,
  frameHeight: number,
  bounds: ImageContentBounds,
) {
  if (imageWidth <= 0 || imageHeight <= 0 || frameWidth <= 0 || frameHeight <= 0 || bounds.width <= 0 || bounds.height <= 0) return null;
  const contentWidth = imageWidth * bounds.width;
  const contentHeight = imageHeight * bounds.height;
  const scale = Math.min(frameWidth / contentWidth, frameHeight / contentHeight);
  return {
    width: imageWidth * scale,
    height: imageHeight * scale,
    left: (frameWidth - contentWidth * scale) / 2 - bounds.x * imageWidth * scale,
    top: (frameHeight - contentHeight * scale) / 2 - bounds.y * imageHeight * scale,
  };
}

// Share the small analysis result across repeated components, never image pixels.
const boundsCache = new Map<string, Promise<ImageContentBounds | null>>();

export function loadImageContentBounds(src: string): Promise<ImageContentBounds | null> {
  // This unchanged manufacturer photo has reviewed white padding. Frame only
  // its complete battery (including both terminals), without treating white
  // pixels in other opaque product photos as removable background. The optional
  // suffix is Vite's eight-character asset hash. Provenance and pixel bounds:
  // assets/original/batt-renogy-mini-core-100-2026-10-16.source.json.
  const filename = src.split(/[?#]/, 1)[0].split("/").pop() ?? "";
  if (/^batt-renogy-mini-core-100-2026-10-16(?:-[\w-]{8})?\.jpg$/.test(filename)) {
    return Promise.resolve({ x: 240 / 1125, y: 330 / 1125, width: 630 / 1125, height: 590 / 1125 });
  }
  const cached = boundsCache.get(src);
  if (cached) return cached;
  const result = new Promise<ImageContentBounds | null>((resolve) => {
    const image = new Image();
    // A separate CORS-enabled image lets the displayed photo keep working even
    // when a custom host does not permit pixel inspection.
    image.crossOrigin = "anonymous";
    image.onload = () => {
      try {
        const scale = Math.min(1, 256 / Math.max(image.naturalWidth, image.naturalHeight));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        const context = canvas.getContext("2d", { willReadFrequently: true });
        if (!context) { resolve(null); return; }
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve(findImageContentBounds(context.getImageData(0, 0, canvas.width, canvas.height).data, canvas.width, canvas.height));
      } catch {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = src;
  });
  // Bound memory when a session visits many custom images.
  if (boundsCache.size >= 256) boundsCache.delete(boundsCache.keys().next().value!);
  boundsCache.set(src, result);
  return result;
}
